// ═══════════════════════════════════════════════════════════════
// CHAPTER SETTINGS SERVICE
// Path: src/services/chapterSettingsService.js
//
// Reads and writes the chapter_settings singleton row and the
// funds reference table. Both are loaded into AppContext on
// login and available globally — they are small, stable, and
// needed everywhere (gift entry dropdowns, receipt generation).
//
// v1.2.0 — Added 6 receipt language fields:
//          receiptThankYou, receiptThankYouDaf, receiptClosing,
//          receiptDafNotice, receiptEmailSubject, receiptEmailBody
// v1.1.0 — Added uploadLogo, uploadSignature, removeFile
//          Added logoUrl to field mappers
// ═══════════════════════════════════════════════════════════════

import { supabase } from "../lib/supabase";
import { debugLog } from "../utils/debugLog";

// ── Default shape returned when no row exists yet ─────────────────
const DEFAULT_CHAPTER_SETTINGS = {
  id: null,
  chapterLegalName: "",
  addressLine1: "",
  addressLine2: "",
  city: "",
  state: "",
  zip: "",
  phone: "",
  email: "",
  ein: "",
  receiptPrefix: "CHAPTER",
  logoUrl: null,
  receiptSignatureUrl: null,
  receiptSignatureName: "",
  receiptSignatureTitle: "",
  receiptFooterNote: "",
  givingLevels: [],
  // Receipt language (blank = use hardcoded defaults)
  receiptThankYou: "",
  receiptThankYouDaf: "",
  receiptClosing: "",
  receiptDafNotice: "",
  receiptEmailSubject: "",
  receiptEmailBody: "",
  updatedAt: null,
};

// ── Field mapping: DB → JS ────────────────────────────────────────
const fromDb = (row) => {
  if (!row) return DEFAULT_CHAPTER_SETTINGS;
  return {
    id: row.id,
    chapterLegalName: row.chapter_legal_name || "",
    addressLine1: row.chapter_address_line1 || "",
    addressLine2: row.chapter_address_line2 || "",
    city: row.chapter_city || "",
    state: row.chapter_state || "",
    zip: row.chapter_zip || "",
    phone: row.chapter_phone || "",
    email: row.chapter_email || "",
    ein: row.ein || "",
    receiptPrefix: row.receipt_prefix || "CHAPTER",
    logoUrl: row.chapter_logo_url || null,
    receiptSignatureUrl: row.receipt_signature_url || null,
    receiptSignatureName: row.receipt_signature_name || "",
    receiptSignatureTitle: row.receipt_signature_title || "",
    receiptFooterNote: row.receipt_footer_note || "",
    givingLevels: row.giving_levels || [],
    receiptThankYou: row.receipt_thank_you || "",
    receiptThankYouDaf: row.receipt_thank_you_daf || "",
    receiptClosing: row.receipt_closing || "",
    receiptDafNotice: row.receipt_daf_notice || "",
    receiptEmailSubject: row.receipt_email_subject || "",
    receiptEmailBody: row.receipt_email_body || "",
    fiscalYearStartMonth: row.fiscal_year_start_month || 1,
    updatedAt: row.updated_at || null,
  };
};

// ── Field mapping: JS → DB ────────────────────────────────────────
const toDb = (data) => {
  const row = {};
  if (data.chapterLegalName !== undefined)
    row.chapter_legal_name = data.chapterLegalName;
  if (data.addressLine1 !== undefined)
    row.chapter_address_line1 = data.addressLine1;
  if (data.addressLine2 !== undefined)
    row.chapter_address_line2 = data.addressLine2;
  if (data.city !== undefined) row.chapter_city = data.city;
  if (data.state !== undefined) row.chapter_state = data.state;
  if (data.zip !== undefined) row.chapter_zip = data.zip;
  if (data.phone !== undefined) row.chapter_phone = data.phone;
  if (data.email !== undefined) row.chapter_email = data.email;
  if (data.ein !== undefined) row.ein = data.ein;
  if (data.receiptPrefix !== undefined) row.receipt_prefix = data.receiptPrefix;
  if (data.logoUrl !== undefined) row.chapter_logo_url = data.logoUrl;
  if (data.receiptSignatureUrl !== undefined)
    row.receipt_signature_url = data.receiptSignatureUrl;
  if (data.receiptSignatureName !== undefined)
    row.receipt_signature_name = data.receiptSignatureName;
  if (data.receiptSignatureTitle !== undefined)
    row.receipt_signature_title = data.receiptSignatureTitle;
  if (data.receiptFooterNote !== undefined)
    row.receipt_footer_note = data.receiptFooterNote;
  if (data.givingLevels !== undefined) row.giving_levels = data.givingLevels;
  if (data.receiptThankYou !== undefined)
    row.receipt_thank_you = data.receiptThankYou;
  if (data.receiptThankYouDaf !== undefined)
    row.receipt_thank_you_daf = data.receiptThankYouDaf;
  if (data.receiptClosing !== undefined)
    row.receipt_closing = data.receiptClosing;
  if (data.receiptDafNotice !== undefined)
    row.receipt_daf_notice = data.receiptDafNotice;
  if (data.receiptEmailSubject !== undefined)
    row.receipt_email_subject = data.receiptEmailSubject;
  if (data.receiptEmailBody !== undefined)
    row.receipt_email_body = data.receiptEmailBody;
  if (data.fiscalYearStartMonth !== undefined)
    row.fiscal_year_start_month = data.fiscalYearStartMonth;
  return row;
};

// ── Fund field mapping ────────────────────────────────────────────
const fundFromDb = (row) => ({
  id: row.id,
  name: row.name,
  code: row.code,
  description: row.description || "",
  isActive: row.is_active,
  sortOrder: row.sort_order,
});

const fundToDb = (data) => {
  const row = {};
  if (data.name !== undefined) row.name = data.name;
  if (data.code !== undefined) row.code = data.code.toUpperCase();
  if (data.description !== undefined) row.description = data.description;
  if (data.isActive !== undefined) row.is_active = data.isActive;
  if (data.sortOrder !== undefined) row.sort_order = data.sortOrder;
  return row;
};

// ── Accepted image types for upload validation ────────────────────
const ACCEPTED_IMAGE_TYPES = [
  "image/png",
  "image/jpeg",
  "image/webp",
  "image/svg+xml",
];
const MAX_IMAGE_SIZE = 2 * 1024 * 1024; // 2 MB

// ═══════════════════════════════════════════════════════════════
// Chapter Settings Service
// ═══════════════════════════════════════════════════════════════
export const chapterSettingsService = {
  // ── Get chapter settings (singleton row) ───────────────────────
  async get() {
    try {
      const { data, error } = await supabase
        .from("chapter_settings")
        .select("*")
        .limit(1)
        .single();

      if (error && error.code !== "PGRST116") throw error;
      return fromDb(data);
    } catch (error) {
      debugLog.error("chapterSettingsService.get", error);
      return DEFAULT_CHAPTER_SETTINGS;
    }
  },

  // ── Update chapter settings ────────────────────────────────────
  async update(updates, userId) {
    try {
      const row = {
        ...toDb(updates),
        updated_by: userId,
      };

      const { data, error } = await supabase
        .from("chapter_settings")
        .upsert(row, { onConflict: "id" })
        .select("*")
        .single();

      if (error) throw error;
      debugLog.action("chapterSettingsService.update", updates);
      return fromDb(data);
    } catch (error) {
      debugLog.error("chapterSettingsService.update", error);
      throw error;
    }
  },

  // ── Upload chapter logo to Supabase Storage ────────────────────
  async uploadLogo(file, chapterId = "default") {
    try {
      _validateImageFile(file);
      const ext = file.name.split(".").pop().toLowerCase();
      const path = `logos/${chapterId}/logo.${ext}`;

      const { error: uploadError } = await supabase.storage
        .from("chapter-assets")
        .upload(path, file, { upsert: true, contentType: file.type });

      if (uploadError) throw uploadError;

      const { data } = supabase.storage
        .from("chapter-assets")
        .getPublicUrl(path);

      const url = `${data.publicUrl}?t=${Date.now()}`;
      debugLog.action("chapterSettingsService.uploadLogo", { path });
      return url;
    } catch (error) {
      debugLog.error("chapterSettingsService.uploadLogo", error);
      throw error;
    }
  },

  // ── Upload signature image to Supabase Storage ─────────────────
  async uploadSignature(file, chapterId = "default") {
    try {
      _validateImageFile(file);
      const ext = file.name.split(".").pop().toLowerCase();
      const path = `signatures/${chapterId}/signature.${ext}`;

      const { error: uploadError } = await supabase.storage
        .from("chapter-assets")
        .upload(path, file, { upsert: true, contentType: file.type });

      if (uploadError) throw uploadError;

      const { data } = supabase.storage
        .from("chapter-assets")
        .getPublicUrl(path);

      const url = `${data.publicUrl}?t=${Date.now()}`;
      debugLog.action("chapterSettingsService.uploadSignature", { path });
      return url;
    } catch (error) {
      debugLog.error("chapterSettingsService.uploadSignature", error);
      throw error;
    }
  },

  // ── Remove a file from Supabase Storage ────────────────────────
  async removeFile(publicUrl) {
    try {
      if (!publicUrl) return;
      const marker = "/chapter-assets/";
      const idx = publicUrl.indexOf(marker);
      if (idx === -1) {
        debugLog.warn(
          "chapterSettingsService.removeFile",
          "Could not parse storage path from URL"
        );
        return;
      }
      let storagePath = publicUrl.substring(idx + marker.length);
      const qIdx = storagePath.indexOf("?");
      if (qIdx !== -1) storagePath = storagePath.substring(0, qIdx);

      const { error } = await supabase.storage
        .from("chapter-assets")
        .remove([storagePath]);

      if (error) throw error;
      debugLog.action("chapterSettingsService.removeFile", { storagePath });
    } catch (error) {
      debugLog.error("chapterSettingsService.removeFile", error);
    }
  },
};

// ── Internal validation helper ────────────────────────────────────
function _validateImageFile(file) {
  if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
    throw new Error(
      `Unsupported file type "${file.type}". Accepted: PNG, JPG, WebP, SVG.`
    );
  }
  if (file.size > MAX_IMAGE_SIZE) {
    throw new Error(
      `File is too large (${(file.size / 1024 / 1024).toFixed(
        1
      )} MB). Maximum: 2 MB.`
    );
  }
}

// ═══════════════════════════════════════════════════════════════
// Funds Service
// ═══════════════════════════════════════════════════════════════
export const fundsService = {
  async getAll() {
    try {
      const { data, error } = await supabase
        .from("funds")
        .select("*")
        .order("is_active", { ascending: false })
        .order("sort_order", { ascending: true })
        .order("name", { ascending: true });

      if (error) throw error;
      return (data || []).map(fundFromDb);
    } catch (error) {
      debugLog.error("fundsService.getAll", error);
      return [];
    }
  },

  async getActive() {
    try {
      const { data, error } = await supabase
        .from("funds")
        .select("*")
        .eq("is_active", true)
        .order("sort_order", { ascending: true })
        .order("name", { ascending: true });

      if (error) throw error;
      return (data || []).map(fundFromDb);
    } catch (error) {
      debugLog.error("fundsService.getActive", error);
      return [];
    }
  },

  async create(fundData) {
    try {
      const { data, error } = await supabase
        .from("funds")
        .insert(fundToDb(fundData))
        .select("*")
        .single();

      if (error) throw error;
      debugLog.action("fundsService.create", { name: fundData.name });
      return fundFromDb(data);
    } catch (error) {
      debugLog.error("fundsService.create", error);
      throw error;
    }
  },

  async update(fundId, updates) {
    try {
      const { data, error } = await supabase
        .from("funds")
        .update(fundToDb(updates))
        .eq("id", fundId)
        .select("*")
        .single();

      if (error) throw error;
      debugLog.action("fundsService.update", { fundId });
      return fundFromDb(data);
    } catch (error) {
      debugLog.error("fundsService.update", error);
      throw error;
    }
  },

  async deactivate(fundId) {
    try {
      const { data, error } = await supabase
        .from("funds")
        .update({ is_active: false })
        .eq("id", fundId)
        .select("*")
        .single();

      if (error) throw error;
      debugLog.action("fundsService.deactivate", { fundId });
      return fundFromDb(data);
    } catch (error) {
      debugLog.error("fundsService.deactivate", error);
      throw error;
    }
  },

  async remove(fundId) {
    try {
      const { error } = await supabase.from("funds").delete().eq("id", fundId);

      if (error) throw error;
      debugLog.action("fundsService.remove", { fundId });
    } catch (error) {
      if (error.code === "23503") {
        throw new Error(
          "This fund has existing gifts and cannot be deleted. Deactivate it instead."
        );
      }
      debugLog.error("fundsService.remove", error);
      throw error;
    }
  },
};
